import React, { useEffect } from "react";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import FormSelectionField from "../../../components/formFields/FormSelectionField";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import ButtonWithLoading from "../../../components/common/ButtonWithLoading";
import { Typography } from "@progress/kendo-react-common";
import { requiredValidator } from "../../../components/formFields/CommonValidator";
import ShadowCard from "../../../components/common/ShadowCard";
import FormDatePicker from "../../../components/formFields/FormDateField";
import moment from "moment";
import { getAllActiveUsers } from "../../user/services/user.services";
import { IUser } from "../../user/userModel";
import { getAllActiveSectors } from "../../sector/services/sector.services";
import { ISector } from "../../sector/sectorModel";
import { BOOKINGSTATUS, INQUIRYQUERIES } from "../../../_contstants/common";
import { getAllFITBookings } from "../../fitbooking/services/FITBooking.services";
import { IFITBooking } from "../../fitbooking/FITBookingModel";

interface ChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const ChangeWatcher: React.FC<ChangeWatcherProps> = ({ formRenderProps }) => {
  useEffect(() => {
    formRenderProps.onChange("filterFromDate", {
      value: moment().startOf("month").toDate(),
    });
    formRenderProps.onChange("filterToDate", {
      value: moment().toDate(),
    });
  }, []);
  return null;
};

const BookingSummaryReportForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.reports.loading);
  const UserList = useAppSelector((state) => state.user.UserList);
  const SectorList = useAppSelector((state) => state.sector.SectorList);
  const FITBookingFilterDetails = useAppSelector(
    (state) => state.FITBooking.FITBookingFilterDetails
  );
  const FITBookingList = useAppSelector(
    (state) => state.FITBooking.FITBookingList
  );
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const UserIDString = useAppSelector(
    (state) => state.login.loginDetails?.UserID
  );
  const UserID = parseInt(UserIDString);

  useEffect(() => {
    dispatch(getAllActiveUsers());
    dispatch(getAllActiveSectors());
    dispatch(
      getAllFITBookings({
        ...FITBookingFilterDetails,
        CreatedBy: UserID,
        Limit: +pageLimit,
        Skip: +currentPage * +pageLimit,
      })
    );
  }, []);

  const handleSubmit = (values: any) => {
    const payload: any = {};

    payload.filterFromDate = values?.filterFromDate
      ? moment(values?.filterFromDate).format("YYYY-MM-DD")
      : "0";
    payload.filterToDate = values?.filterToDate
      ? moment(values?.filterToDate).format("YYYY-MM-DD")
      : "0";
    payload.filterCreatedBy = values?.filterCreatedBy
      ? values?.filterCreatedBy
      : "0";
    payload.filterSector = values?.filterSector ? values?.filterSector : "0";
    payload.CustomerName = values?.CustomerName ? values?.CustomerName : "0";
    payload.ConfirmStatus = values?.ConfirmStatus ? values?.ConfirmStatus : "0";
    payload.filterRequirementType = values?.filterRequirementType
      ? values?.filterRequirementType
      : "0";

    window.open(
      `bookingsummaryreportview/${payload?.filterFromDate}/${payload?.filterToDate}/${payload?.filterSector}/${payload?.filterCreatedBy}/${payload?.CustomerName}/${payload?.ConfirmStatus}/${payload?.filterRequirementType}`
    );
  };

  return (
    <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
      <GridLayoutItem>
        <ShadowCard>
          <Form
            onSubmit={handleSubmit}
            render={(formRenderProps: FormRenderProps) => (
              <FormElement style={{ width: "100%" }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ cols: 10, rows: 0 }}
                  cols={[{ width: "40%" }, { width: "30%" }, { width: "30%" }]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4
                      style={{ marginBottom: 0, marginLeft: "3px" }}
                    >
                      Booking Summary Report
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem style={{ display: "flex", gap: 10 }}>
                    <Field
                      name="filterFromDate"
                      label="From Date"
                      format="dd/MM/yyyy"
                      wrapperStyle={{ width: "100%" }}
                      component={FormDatePicker}
                      astrike={true}
                      validator={requiredValidator}
                    />
                    <Field
                      name="filterToDate"
                      label="To Date"
                      format="dd/MM/yyyy"
                      minDate={moment(
                        formRenderProps.valueGetter("filterFromDate")
                      ).toDate()}
                      wrapperStyle={{ width: "100%" }}
                      component={FormDatePicker}
                      astrike={true}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}></GridLayoutItem>
                  <GridLayoutItem style={{ display: "flex", gap: 10 }}>
                    <Field
                      name="filterRequirementType"
                      wrapperClassName="w-50"
                      label="Service Type"
                      placeholder="Type"
                      component={FormSelectionField}
                      options={INQUIRYQUERIES?.map((type: any) => ({
                        value: type?.value,
                        label: type?.label,
                      }))}
                    />
                    <Field
                      name="ConfirmStatus"
                      wrapperClassName="w-50"
                      label="Booking Status"
                      placeholder="Select Status"
                      component={FormSelectionField}
                      options={BOOKINGSTATUS?.map((type: any) => ({
                        value: type?.value,
                        label: type?.label,
                      }))}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}></GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="CustomerName"
                      label="Customer Name"
                      placeholder="Customer Name"
                      wrapperStyle={{ width: "100%" }}
                      component={FormSelectionField}
                      options={FITBookingList?.filter(
                        (Customer, index, array) =>
                          `${Customer?.FirstName} ${Customer?.LastName}` !==
                          `${array[index - 1]?.FirstName} ${
                            array[index - 1]?.LastName
                          }`
                      )?.map((Customer: IFITBooking) => ({
                        value: `${Customer?.FirstName} ${Customer?.LastName}`,
                        label: `${Customer?.FirstName} ${Customer?.LastName}`,
                      }))}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}></GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="filterSector"
                      label="Destination"
                      placeholder="Destination"
                      wrapperStyle={{ width: "100%" }}
                      component={FormSelectionField}
                      options={SectorList?.map((sector: ISector) => ({
                        value: sector?.ID,
                        label: sector?.SectorName,
                      }))}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}></GridLayoutItem>
                  <GridLayoutItem style={{ width: "100%" }}>
                    <Field
                      name="filterCreatedBy"
                      label="Created By"
                      placeholder="Created By"
                      component={FormSelectionField}
                      options={UserList?.map((user: IUser) => ({
                        value: user?.ID,
                        label: `${user?.FirstName} ${user?.LastName}`,
                      }))}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}></GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      display: "flex",
                      gap: 10,
                      position: "relative",
                      minHeight: 50,
                    }}
                  >
                    <div style={{ position: "absolute", top: 20, right: 0 }}>
                      <ButtonWithLoading
                        label={"Get Details"}
                        type="submit"
                        Size="medium"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                    </div>
                  </GridLayoutItem>
                </GridLayout>
                <ChangeWatcher formRenderProps={formRenderProps} />
              </FormElement>
            )}
          />
        </ShadowCard>
      </GridLayoutItem>
    </GridLayout>
  );
};

export default BookingSummaryReportForm;
